import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import LanguageContext from '../contexts/LanguageContext';
import { getTranslation } from '../services/languageService';
import { getEnabledPortfolioItems } from '../services/portfolioService';
import shouldUseMockData from '../services/apiConfig';
import Markdown from '../components/Markdown';
import './Portfolio.scss';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const Portfolio = () => {
  const { language } = useContext(LanguageContext);
  const [portfolioItems, setPortfolioItems] = useState([]);

  useEffect(() => {
    if (shouldUseMockData()) {
      setPortfolioItems(getEnabledPortfolioItems(language));
      return;
    }

    const fetchPortfolio = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/portfolio?lang=${language}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const items = (data.portfolio || [])
          .filter(item => item.enabled !== false)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setPortfolioItems(items);
      } catch (error) {
        console.error('Failed to load portfolio items:', error);
        setPortfolioItems(getEnabledPortfolioItems(language));
      }
    };

    fetchPortfolio();
  }, [language]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <motion.div
      className="portfolio"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <section className="section">
        <div className="container">
          <motion.h1 className="page-title text-center" variants={itemVariants}>
            {getTranslation('navigation.portfolio', language)}
          </motion.h1>

          <div className="portfolio-grid">
            {portfolioItems.map((item) => (
              <motion.div
                key={item.id}
                className="portfolio-card card"
                variants={itemVariants}
                whileHover={{ y: -10 }}
              >
                {item.image && (
                  <div className="portfolio-image">
                    <img src={item.image} alt={item.title} />
                  </div>
                )}
                <div className="portfolio-content">
                  <span className="portfolio-category">{item.category}</span>
                  <h2 className="portfolio-title">{item.title}</h2>
                  <Markdown className="portfolio-description">{item.description}</Markdown>
                  <div className="portfolio-results">
                    <strong>Results:</strong> <Markdown inline>{item.results}</Markdown>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Portfolio;
